// PATH: src/config/config.ts

// ---------- Types & Runtime config ----------
export type ApiService = "identity" | "embed" | "upload" | "learn" | "llm";

type RuntimeConfig = Partial<{
    API_URL: string;
    API_BASE_URL: string; // on supporte les deux clés
    APP_VERSION: string;
    TILMIDPLUS_URL: string;
    API_TIMEOUT: string | number;
    MODE: string,
    // Surcharges optionnelles des préfixes par service côté runtime
    IDENTITY_PREFIX: string;
    EMBED_PREFIX: string;
    UPLOAD_PREFIX: string;
    LEARN_PREFIX: string;
    LLM_PREFIX: string;
}>;

// 1) runtimeConfig éventuel injecté côté HTML (public/config.js)
const rc: RuntimeConfig =
    (typeof window !== "undefined" && (window as any).runtimeConfig) || {};

// 2) CRA (react-scripts): variables injectées au build
const env = (process.env as Record<string, string | undefined>) || {};

// ---------- Helpers ----------
const pickStr = (...vals: Array<string | undefined | null>): string => {
    for (const v of vals) {
        if (typeof v === "string" && v.trim() !== "") return v;
    }
    return "";
};

const pickNum = (...vals: Array<string | number | undefined | null>): number => {
    for (const v of vals) {
        if (typeof v === "number" && !Number.isNaN(v)) return v;
        if (typeof v === "string" && v.trim() !== "") {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
        }
    }
    return 100000; // défaut
};

const trimTrailingSlashes = (url: string) => url.replace(/\/+$/, "");

// ---------- Base host (commun à tous les services) ----------
export const apiBaseUrl: string = trimTrailingSlashes(
    // En développement, l'env (.env.local, ligne de commande) prime sur
    // public/config.js, qui pointe toujours sur l'UAT.
    env.NODE_ENV === "development"
        ? pickStr(env.REACT_APP_API_BASE_URL, rc.API_URL, rc.API_BASE_URL)
        : pickStr(rc.API_URL, rc.API_BASE_URL, env.REACT_APP_API_BASE_URL)
);

// ---------- Préfixes par service (actifs hors développement) ----------
// const RUNTIME_ENV = env.NODE_ENV || process.env.NODE_ENV || "development";
// const isDev = RUNTIME_ENV === "development";
const isDev = rc.MODE === "dev";

// Surcharges possibles via runtimeConfig OU .env
const IDENTITY_PREFIX = pickStr(
    rc.IDENTITY_PREFIX,
    env.REACT_APP_IDENTITY_PREFIX,
    "/tc_identity"
);
const EMBED_PREFIX = pickStr(
    rc.EMBED_PREFIX,
    env.REACT_APP_EMBED_PREFIX,
    "/tc_embedbe"
);
const UPLOAD_PREFIX = pickStr(
    rc.UPLOAD_PREFIX,
    env.REACT_APP_UPLOAD_PREFIX,
    "/tc_upload"
);
// Le moteur de quêtes (tc_learn), joint par le broker comme les autres
// services : la file « Quêtes à corriger » vit chez lui, pas dans tc_embed_be.
const LEARN_PREFIX = pickStr(
    rc.LEARN_PREFIX,
    env.REACT_APP_LEARN_PREFIX,
    "/tc_learn"
);

// La passerelle IA (tc_llm_gateway), jointe par le broker : seule détentrice
// des clés des fournisseurs. L'écran « Fournisseurs » lui envoie une clé
// directement, sans qu'elle passe par tc_embed_be.
const LLM_PREFIX = pickStr(
    rc.LLM_PREFIX,
    env.REACT_APP_LLM_PREFIX,
    "/tc_llm"
);

// Table de mapping des services → préfixes (ignorés en dev)
const SERVICE_PREFIX: Record<ApiService, string> = {
    identity: isDev ? "" : IDENTITY_PREFIX,
    embed: isDev ? "" : EMBED_PREFIX,
    upload: isDev ? "" : UPLOAD_PREFIX,
    learn: isDev ? "" : LEARN_PREFIX,
    llm: isDev ? "" : LLM_PREFIX,
};

// ---------- Constructeur d'URL par service ----------
export const buildBaseUrl = (service: ApiService): string => {
    const base = apiBaseUrl || "http://localhost:8080";
    const prefix = SERVICE_PREFIX[service] || "";
    return `${trimTrailingSlashes(base)}${prefix}`;
};


// ---------- Divers (inchangé) ----------
export const appVersion: string = pickStr(
    rc.APP_VERSION,
    env.REACT_APP_VERSION,
    "dev"
);

export const tilmidPlusURL: string = pickStr(
    rc.TILMIDPLUS_URL,
    env.REACT_APP_TC_CHAT_UI_URL,
    "https://api.tilmicode.com/"
);

export const apiTimeout: number = pickNum(
    rc.API_TIMEOUT as any,
    env.REACT_APP_API_TIMEOUT
);

// ---------- Alerte utile en dev ----------
if (!apiBaseUrl) {
    // eslint-disable-next-line no-console
    console.warn("[config] apiBaseUrl est vide. Vérifie public/config.js et .env");
}
